import React, { useEffect, useRef, useState } from "react";
import "../styles/lab/assistant.css";
import { discuter, getEtatAssistant } from "../services/lab/assistantService";

/**
 * Widget d'assistant, disponible depuis n'importe quel écran.
 *
 * L'historique vit ici, dans le composant, et repart à chaque question. Le Lab
 * le gardait dans une Map en mémoire du serveur : il était perdu à chaque
 * redémarrage et rendait impossible tout déploiement sur plusieurs instances.
 */
export default function Assistant() {
  const [ouvert, setOuvert]       = useState(false);
  const [etat, setEtat]           = useState(null);
  const [messages, setMessages]   = useState([]);
  const [saisie, setSaisie]       = useState("");
  const [attente, setAttente]     = useState(false);
  const [indispo, setIndispo]     = useState(null);
  const finRef = useRef(null);

  useEffect(() => {
    if (ouvert && !etat) {
      getEtatAssistant().then(setEtat).catch(() => setIndispo("Assistant injoignable."));
    }
  }, [ouvert, etat]);

  useEffect(() => {
    finRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, attente]);

  const envoyer = async (texte) => {
    const question = (texte ?? saisie).trim();
    if (!question || attente) return;

    setSaisie("");
    setIndispo(null);
    const historique = messages.map((m) => ({ role: m.role, content: m.content }));
    setMessages((m) => [...m, { role: "user", content: question }]);
    setAttente(true);

    try {
      const r = await discuter(question, historique);
      setMessages((m) => [...m, { role: "assistant", content: r.reponse }]);
    } catch (err) {
      // 503 : le fournisseur est indisponible (clé, quota, service arrêté).
      // On affiche le motif réel au lieu d'un « une erreur est survenue ».
      const motif = err.response?.data?.error || "L'assistant est momentanément indisponible.";
      setIndispo(motif);
      setMessages((m) => m.slice(0, -1));
      setSaisie(question);
    } finally {
      setAttente(false);
    }
  };

  if (!ouvert) {
    return (
      <button className="assistant-bulle" onClick={() => setOuvert(true)} title="Assistant">
        <i className="fa-solid fa-robot"></i>
      </button>
    );
  }

  return (
    <div className="assistant-panneau">
      <header className="assistant-tete">
        <div>
          <strong><i className="fa-solid fa-robot"></i> Assistant</strong>
          {etat && <span className="assistant-modele">{etat.fournisseur} · {etat.modele}</span>}
        </div>
        <div className="assistant-tete-actions">
          {messages.length > 0 && (
            <button onClick={() => setMessages([])} title="Effacer la conversation">
              <i className="fa-solid fa-broom"></i>
            </button>
          )}
          <button onClick={() => setOuvert(false)} title="Fermer">
            <i className="fa-solid fa-xmark"></i>
          </button>
        </div>
      </header>

      <div className="assistant-corps">
        {!messages.length && (
          <div className="assistant-accueil">
            <p>
              Posez une question sur l'ERP. Je réponds à partir des données
              auxquelles <strong>votre rôle</strong> donne accès.
            </p>
            {etat?.suggestions?.map((s) => (
              <button key={s} className="assistant-suggestion" onClick={() => envoyer(s)}>
                {s}
              </button>
            ))}
          </div>
        )}

        {messages.map((m, i) => (
          <div key={i} className={`assistant-msg ${m.role}`}>
            {m.role === "assistant" && <i className="fa-solid fa-robot assistant-avatar"></i>}
            <div className="assistant-bulle-msg">{m.content}</div>
          </div>
        ))}

        {attente && (
          <div className="assistant-msg assistant">
            <i className="fa-solid fa-robot assistant-avatar"></i>
            <div className="assistant-bulle-msg assistant-attente">
              <span></span><span></span><span></span>
            </div>
          </div>
        )}

        {indispo && (
          <div className="assistant-indispo">
            <i className="fa-solid fa-triangle-exclamation"></i> {indispo}
          </div>
        )}

        <div ref={finRef} />
      </div>

      <form className="assistant-saisie" onSubmit={(e) => { e.preventDefault(); envoyer(); }}>
        <input
          value={saisie}
          onChange={(e) => setSaisie(e.target.value)}
          placeholder="Votre question…"
          disabled={attente}
        />
        <button type="submit" disabled={attente || !saisie.trim()}>
          <i className="fa-solid fa-paper-plane"></i>
        </button>
      </form>
    </div>
  );
}
